import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import shopping_cart from "../../assets/images/icons/Shopping_cart.svg";
import search from "../../assets/images/icons/Search.svg";
import account_circle from "../../assets/images/icons/Account_circle.svg";
import { useAuth } from "../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import { useCart } from "../../context/CartContext";
import { HeaderMobileLoginPanel } from "./HeaderMobileLoginPanel";
import { HeaderMobileCartPanel } from "./HeaderMobileCartPanel";
import { HeaderSearchPanel } from "./HeaderSearchPanel";
import { useHeaderSearch } from "../../hooks/useHeaderSearch";
import { useLocalizedNavigation } from "../../hooks/useLocalizedNavigation";
import { withLocalePath } from "../../utils/localeRouting";

export const HeaderMobileMenu = ({ menuOpen, setMenuOpen, query, setQuery }) => {
  const toggleMenu = () => setMenuOpen((prev) => !prev);
  const { isAuthenticated, language, setLanguage, user } = useAuth();
  const { cartItems } = useCart();
  const { t } = useTranslation("header");
  const navigate = useNavigate();
  const location = useLocation();
  const { localizePath, pathnameWithoutLocale } = useLocalizedNavigation();
  const isSnapshot =
    typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";

  const [activePanel, setActivePanel] = useState(null);
  const { results, isLoading } = useHeaderSearch(
    query,
    menuOpen || activePanel === "search"
  );

  useEffect(() => {
    setMenuOpen(false);
    setActivePanel(null);
  }, [location.pathname, setMenuOpen]);

  const quickLinks = [
    { label: t("products"), path: localizePath("/products") },
    { label: t("contacts"), path: localizePath("/contacts") },
    { label: t("aboutUs"), path: localizePath("/aboutus") },
    { label: t("downloadArea"), path: localizePath("/downloadarea") },
    { label: t("recruitment"), path: localizePath("/recruitment") },
    { label: t("news"), path: localizePath("/news") },
  ].filter((item) => !(isSnapshot && item.path.endsWith("/recruitment")));

  const getInitials = (fullName) => {
    if (!fullName || typeof fullName !== "string") {
      return "";
    }

    const names = fullName.trim().split(/\s+/);
    if (names.length === 0) return "";
    if (names.length === 1) return names[0][0]?.toUpperCase() || "";

    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  };

  const navItems = [
    { name: t("products"), link: "products" },
    { name: t("aboutUs"), link: "aboutus" },
    { name: t("downloadArea"), link: "downloadarea" },
    { name: t("recruitment"), link: "recruitment" },
    { name: t("contacts"), link: "contacts" },
    { name: t("news"), link: "news" },
  ].filter((item) => !(isSnapshot && item.link === "recruitment"));

  return (
    <li className="header__hamburger">
      <div
        className={`hamburger-icon ${menuOpen ? "open" : ""}`}
        onClick={toggleMenu}
      >
        <span></span>
        <span></span>
      </div>

      <div className={`menu-pane ${menuOpen ? "active" : ""}`}>
        <nav>
          <ul className="menu-links">
            <div className="menu-links__icons">
              <li style={{ padding: "0 8px" }}>
                <select
                  className="language-selector"
                  value={language}
                  onChange={(event) => {
                    const nextLanguage = event.target.value;
                    setLanguage(nextLanguage);
                    navigate(withLocalePath(pathnameWithoutLocale, nextLanguage));
                  }}
                >
                  <option value="pt">PT</option>
                  <option value="en">EN</option>
                  <option value="es">ES</option>
                  <option value="fr">FR</option>
                </select>
              </li>

              <li
                style={{ position: "relative" }}
                onClick={() => setActivePanel((prev) => (prev === "cart" ? null : "cart"))}
              >
                <img src={shopping_cart} alt="Shopping Cart" />
                {cartItems.length > 0 && <span className="cart-badge">{cartItems.length}</span>}
              </li>

              <li
                onClick={() => {
                  if (isAuthenticated) {
                    navigate(localizePath("/account"));
                    return;
                  }

                  setActivePanel((prev) => (prev === "auth" ? null : "auth"));
                }}
              >
                {isAuthenticated ? (
                  <p className="user-initials">{getInitials(user?.fullName)}</p>
                ) : (
                  <img src={account_circle} alt="Account" />
                )}
              </li>

              <li
                onClick={() => setActivePanel((prev) => (prev === "search" ? null : "search"))}
                className={activePanel === "search" ? "active" : ""}
              >
                <img src={search} alt="Search" />
              </li>
            </div>

            <li
              className={`mobile__panel-li ${
                activePanel === "search" ? "mobile__panel-li--visible" : ""
              }`}
            >
              <div
                className={`mobile__search ${
                  activePanel === "search" ? "mobile__search--open" : ""
                }`}
              >
                <HeaderSearchPanel
                  query={query}
                  setQuery={setQuery}
                  quickLinks={quickLinks}
                  results={results}
                  isLoading={isLoading}
                  autoFocus={activePanel === "search"}
                  onClose={() => {
                    if (activePanel === "search") {
                      setQuery("");
                      setActivePanel(null);
                    }
                  }}
                  onSelect={() => {
                    setQuery("");
                    setActivePanel(null);
                    setMenuOpen(false);
                  }}
                />
              </div>
            </li>

            <li
              className={`mobile__panel-li ${
                activePanel === "auth" ? "mobile__panel-li--visible" : ""
              }`}
            >
              <HeaderMobileLoginPanel
                isOpen={activePanel === "auth"}
                close={() => setActivePanel(null)}
              />
            </li>

            <li
              className={`mobile__panel-li ${
                activePanel === "cart" ? "mobile__panel-li--visible" : ""
              }`}
            >
              <HeaderMobileCartPanel
                isOpen={activePanel === "cart"}
                close={() => setActivePanel(null)}
              />
            </li>

            {navItems.map((item) => (
              <Link
                key={item.link}
                to={localizePath(`/${item.link}`)}
                onClick={() => setMenuOpen(false)}
              >
                <li className="header__link">{item.name}</li>
              </Link>
            ))}
          </ul>
        </nav>
      </div>
    </li>
  );
};
